import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Eye, FileText, Zap, Shield,
  Key, Users, Laptop, Lock, XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';

const autonomyLadder = [
  {
    level: 'Observe',
    icon: Eye,
    variant: 'neutral',
    description: 'Monitor signals and gather evidence. No write operations. Use for environment baselining.',
    examples: ['New hire detected in HRIS', 'Inactive SaaS seat identified', 'Compliance drift observed']
  },
  {
    level: 'Propose',
    icon: FileText,
    variant: 'pending',
    description: 'Generate action bundles, route for approval. All actions require human review before execution.',
    examples: ['Onboarding bundle awaits approval', 'Access request routed to owner', 'Compliance fix proposed']
  },
  {
    level: 'Execute',
    icon: Zap,
    variant: 'active',
    description: 'Execute automatically within defined guardrails. Escalate when boundaries are exceeded.',
    examples: ['Standard onboarding executes', 'Password reset completes', 'License reclaim runs']
  },
  {
    level: 'Full Autonomy',
    icon: Shield,
    variant: 'info',
    description: 'Trusted workflows with >99% verification pass rate and <1% rollback rate over 30+ executions.',
    examples: ['High-confidence workflows', 'Requires explicit enablement', 'Continuous drift monitoring']
  }
];

const domainToggles = [
  { domain: 'Identity', description: 'Okta, Entra ID, Google Workspace', defaultMode: 'Execute' },
  { domain: 'Endpoints', description: 'Intune, Jamf', defaultMode: 'Propose' },
  { domain: 'SaaS', description: 'App access, licenses', defaultMode: 'Execute' },
  { domain: 'Security', description: 'Containment, quarantine', defaultMode: 'Observe' }
];

const actionClassGates = [
  { action: 'Grant access', gate: 'Standard: auto. Elevated: approval required.', icon: Key },
  { action: 'Revoke access', gate: 'Standard: auto. Admin/executive: approval required.', icon: XCircle },
  { action: 'Install software', gate: 'Approved list: auto. Others: approval required.', icon: Laptop },
  { action: 'Quarantine device', gate: 'Always requires security approval.', icon: Shield },
  { action: 'Rotate credentials', gate: 'User creds: auto. Shared/service: approval required.', icon: Lock }
];

const guardrailTypes = [
  {
    name: 'Approval Tokens',
    description: 'Scoped permissions with time, resource, and use limits.',
    example: 'Grant jsmith@acme.com access to prod-db-admin for 8 hours, single use.'
  },
  {
    name: 'Rate Limits',
    description: 'Per-case-type limits to prevent runaway automation.',
    example: 'Max 50 onboardings/day, max 100 access grants/hour.'
  },
  {
    name: 'Budget Caps',
    description: 'Cost caps per workflow to control spend.',
    example: 'Onboarding budget: $500/month. Alert at 80%, stop at 100%.'
  },
  {
    name: 'Kill Switch',
    description: 'Instant pause for all automation. One-click emergency stop.',
    example: 'Pause all Identity and SaaS automation on security incident.'
  }
];

export const Autonomy = () => {
  const [domainModes, setDomainModes] = useState({
    Identity: 'Execute',
    Endpoints: 'Propose',
    SaaS: 'Execute',
    Security: 'Observe'
  });

  const toggleMode = (domain) => {
    const modes = ['Observe', 'Propose', 'Execute'];
    const currentIndex = modes.indexOf(domainModes[domain]);
    const nextIndex = (currentIndex + 1) % modes.length;
    setDomainModes({ ...domainModes, [domain]: modes[nextIndex] });
  };

  const getModeClass = (mode) => {
    switch (mode) {
      case 'Observe': return 'badge-neutral';
      case 'Propose': return 'badge-pending';
      case 'Execute': return 'badge-active';
      default: return 'badge-info';
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="autonomy-page">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-xs">V</span>
              </div>
              <span className="text-sm font-semibold text-slate-900">Vedaya</span>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/workflows" className="text-sm text-muted-foreground hover:text-slate-900">Workflows</Link>
              <Link to="/autonomy" className="text-sm font-medium text-primary">Autonomy</Link>
              <Link to="/integrations" className="text-sm text-muted-foreground hover:text-slate-900">Integrations</Link>
              <Link to="/evidence" className="text-sm text-muted-foreground hover:text-slate-900">Evidence</Link>
              <Link to="/security-governance" className="text-sm text-muted-foreground hover:text-slate-900">Security</Link>
              <Link to="/pricing" className="text-sm text-muted-foreground hover:text-slate-900">Pricing</Link>
            </nav>
            <Button asChild size="sm">
              <Link to="/control-tower">Open Control Tower</Link>
            </Button>
          </div>
        </div>
      </header>

      <section className="py-10 px-6 border-b border-border">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">Autonomy Configuration</h1>
          <p className="text-slate-600">
            Control automation levels by domain, workflow, and risk tier.
            Four autonomy levels with per-domain toggles and per-workflow overrides.
          </p>
        </div>
      </section>

      <section className="py-10 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Autonomy Levels</h2>

          <div className="grid md:grid-cols-4 gap-4">
            {autonomyLadder.map((level) => (
              <div
                key={level.level}
                className="rounded-lg border border-slate-200 p-4 bg-white"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-7 h-7 rounded flex items-center justify-center ${
                    level.variant === 'neutral' ? 'bg-slate-100' :
                    level.variant === 'pending' ? 'bg-slate-200' :
                    level.variant === 'active' ? 'bg-primary/10' :
                    'bg-primary'
                  }`}>
                    <level.icon className={`w-4 h-4 ${
                      level.variant === 'neutral' ? 'text-slate-600' :
                      level.variant === 'pending' ? 'text-slate-600' :
                      level.variant === 'active' ? 'text-primary' :
                      'text-white'
                    }`} />
                  </div>
                  <span className="text-sm font-semibold text-slate-900">{level.level}</span>
                </div>
                <p className="text-sm text-slate-600 mb-3">{level.description}</p>
                <div className="text-xs text-slate-500">
                  <div className="font-medium text-slate-600 mb-1">Examples:</div>
                  <ul className="space-y-0.5">
                    {level.examples.map((ex, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <span className="text-slate-400">•</span>
                        {ex}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 px-6 bg-slate-50 border-y border-border">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Controls</h2>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-500" />
                Per-Domain Toggles
              </h3>
              <div className="space-y-2">
                {domainToggles.map((d) => (
                  <div key={d.domain} className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{d.domain}</div>
                      <div className="text-xs text-slate-500">{d.description}</div>
                    </div>
                    <button
                      onClick={() => toggleMode(d.domain)}
                      className={`badge-status ${getModeClass(domainModes[d.domain])} cursor-pointer hover:opacity-80`}
                    >
                      {domainModes[d.domain]}
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-3">Click to cycle modes. In production, persists to policy configuration.</p>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-slate-500" />
                Per-Action Gates
              </h3>
              <div className="space-y-2">
                {actionClassGates.map((a) => (
                  <div key={a.action} className="flex items-start gap-3 p-3 bg-slate-50 rounded-md">
                    <a.icon className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-slate-900">{a.action}</div>
                      <div className="text-xs text-slate-500">{a.gate}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Guardrails</h2>

          <div className="grid md:grid-cols-2 gap-4">
            {guardrailTypes.map((g) => (
              <div key={g.name} className="bg-white rounded-lg border border-slate-200 p-4">
                <h3 className="text-sm font-semibold text-slate-900 mb-1">{g.name}</h3>
                <p className="text-sm text-slate-600 mb-3">{g.description}</p>
                <div className="bg-slate-50 rounded-md p-3">
                  <div className="text-xs text-slate-500 font-medium mb-1">Example:</div>
                  <div className="text-xs text-slate-700 font-mono">{g.example}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 px-6 bg-slate-50 border-t border-border">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Configure autonomy policies</h2>
            <p className="text-sm text-slate-600">Set domain-level modes, action gates, and guardrail thresholds.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild variant="outline">
              <Link to="/workflows">View Workflows</Link>
            </Button>
            <Button asChild>
              <Link to="/control-tower">
                Open Control Tower
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <footer className="py-6 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-2xs">V</span>
            </div>
            <span className="text-xs text-slate-500">Vedaya AI</span>
          </div>
          <p className="text-xs text-slate-400">© 2026 Vedaya AI Inc.</p>
        </div>
      </footer>
    </div>
  );
};

export default Autonomy;
